import { RepeatIcon } from '@chakra-ui/icons'
import {
  Box,
  BoxProps,
  Button,
  Collapse,
  Flex,
  FlexProps,
  IconButton,
  Stack,
  StackProps,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import {
  AnyObject,
  Field,
  FieldMap,
  InputSize,
  isField,
  ListField,
  ShareCorrections,
  UploadProgress,
  UserGroup,
} from '@hb/shared'

import { ThemeContext, ThemeContextData } from '@hb/react'
import { FORM_ERROR, FormApi } from 'final-form'
import React, {
  CSSProperties,
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFormState } from 'react-final-form'
import { useResizeObserver } from '../../../hooks/useResizeObserver'
import { ActionLog } from '../../ActionLog'
import { SolidActionButton } from '../../Buttons'
import { CollapseError } from '../../CollapseError'
// import { CorrectionsProvider, ShareCorrectionsProvider } from '../../DataView'
import { CorrectionsProvider, ShareCorrectionsProvider } from '../../DataView/context'
import { CollapseHorizontal } from '../../shared'
import { FormElement } from '../Input'
import { useForm } from './FormProvider'
import { InnerForm } from './InnerForm'
import { SubmitOnEnter } from './SubmitOnEnter'
import { UploadProgressView } from './UploadProgressView'
import { OnFormSubmit } from './types'

export const SimpleFormFooter = ({
  onCancel,
  submitText,
  submitCleanText,
  hasResetButton,
  uploads,
  optional,
  canSubmitClean,
  canSubmitInvalid,
  submitInvalidText,
  children,
  theme,
  flexProps,
}: PropsWithChildren<{
  onCancel?: () => void
  submitText: string
  hasResetButton?: boolean
  canSubmitClean?: boolean
  canSubmitInvalid?: boolean
  submitInvalidText?: string
  submitCleanText?: string
  uploads?: Record<string, UploadProgress> | null
  theme?: 'basic' | 'detailed'
  flexProps?: FlexProps
  optional?: boolean
}>) => {
  const { dirty, values, submitting, hasValidationErrors, errors, submitErrors } = useFormState({
    subscription: {
      dirty: true,
      values: true,
      submitting: true,
      hasValidationErrors: true,
      errors: true,
      submitErrors: true,
    },
  })
  const form = useForm()
  const stackRef = useRef<HTMLDivElement>(null)
  const formError = useMemo(
    () => errors?.[FORM_ERROR] || submitErrors?.[FORM_ERROR],
    [errors, submitErrors],
  )
  const displayedSubmitText = useMemo(() => {
    if (canSubmitClean && !dirty) return submitCleanText || submitText
    if (canSubmitInvalid && hasValidationErrors) return submitInvalidText || submitText
    return submitText
  }, [
    canSubmitClean,
    submitCleanText,
    submitText,
    dirty,
    canSubmitInvalid,
    hasValidationErrors,
    submitInvalidText,
  ])

  const canSubmit = useMemo(() => {
    if (!canSubmitClean && !dirty) return false
    if (!canSubmitInvalid && hasValidationErrors) return false
    return true
  }, [canSubmitClean, dirty, canSubmitInvalid, hasValidationErrors])

  const submit = useCallback(async () => {
    if (hasValidationErrors && canSubmitInvalid) return form.saveWithoutValidation()
    return form.submit()
  }, [form, hasValidationErrors, canSubmitInvalid])

  const { width: buttonWidth } = useResizeObserver(stackRef, 'content')
  return (
    <Flex
      w="100%"
      flexDir="column"
      bg="gray.100"
      borderTop="1px solid"
      borderColor="gray.200"
      _dark={{ bg: 'gray.900', borderColor: 'gray.700' }}
      {...flexProps}>
      <CollapseError error={formError} />
      <UploadProgressView uploads={uploads} />
      <Flex px={theme === 'detailed' ? 3 : 0} alignItems="center" gap={2} w="100%">
        <Box flex={1}>{children}</Box>
        {onCancel ? (
          <Box py={2}>
            <Button onClick={onCancel} variant="link" size="xs" position="relative" bottom="2px">
              CANCEL
            </Button>
          </Box>
        ) : null}
        <Collapse
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            display: 'flex',
          }}
          in={!!((values || optional) && (dirty || canSubmitClean))}>
          <CollapseHorizontal
            width={buttonWidth + 8}
            in={!!((values || optional) && (dirty || canSubmitClean))}>
            <Stack ref={stackRef} align="center" direction="row" gap={3} py={2}>
              {hasResetButton ? (
                <Tooltip hasArrow bg="whitesmoke" color="gray.700" label="Reset" placement="top">
                  <IconButton
                    aria-label="reset"
                    icon={<RepeatIcon w={3} h={3} />}
                    bg="white"
                    opacity={dirty || canSubmitClean ? 1 : 0}
                    onClick={() => form.restart()}
                    size="xs"
                    fontSize="10px"
                    lineHeight="10px"
                    border="1px solid #999999"
                    color="gray.500"
                  />
                </Tooltip>
              ) : null}
              <SolidActionButton
                opacity={!canSubmit ? 0.5 : 1}
                isLoading={submitting}
                onClick={submit}
                size="sm"
                fontSize="14px"
                lineHeight="12px">
                {displayedSubmitText}
              </SolidActionButton>
            </Stack>
          </CollapseHorizontal>
        </Collapse>
      </Flex>
    </Flex>
  )
}

export interface SimpleFormProps<T extends AnyObject> {
  value?: T | null
  onSubmit: OnFormSubmit<T>
  children?: React.ReactNode
  field: Field | FieldMap | ListField
  fieldStyle?: CSSProperties
  canSubmitClean?: boolean
  placeholderAbove?: boolean
  readOnly?: boolean
  boxProps?: StackProps
  corrections?: T & { correctionsShared?: ShareCorrections | null }
  adminView?: boolean
  closeOnSuccess?: boolean
  theme?: 'basic' | 'detailed'
  size?: InputSize
  submitOnEnter?: boolean
  onCancel?: () => void
  BodyContainer?: FC<PropsWithChildren>
  bodyContainerProps?: BoxProps
  submitText?: string
  submitCleanText?: string
  hasResetButton?: boolean
  hideHints?: boolean
  hideLabels?: boolean
  footerFlexProps?: FlexProps
  registerForm?: (form: FormApi<T> | null) => void
}

export const basicFooterFlexProps: FlexProps = {
  bg: 'transparent',
  _dark: { bg: 'transparent' },
  borderTop: '0px solid',
  px: 0,
  py: 0,
}

export const SimpleForm = <T extends AnyObject>({
  value,
  onSubmit,
  field,
  boxProps,
  children,
  submitText = 'SUBMIT',
  submitCleanText = 'CONFIRM',
  onCancel,
  fieldStyle,
  readOnly,
  theme = 'detailed',
  size,
  closeOnSuccess,
  canSubmitClean,
  hasResetButton = true,
  submitOnEnter,
  corrections,
  bodyContainerProps,
  placeholderAbove = true,
  hideHints,
  hideLabels,
  BodyContainer,
  footerFlexProps,
  registerForm,
}: SimpleFormProps<T>) => {
  const { updatedOn, updatedBy, updatedByGroup, ...data } = (value ?? {}) as T & {
    updatedOn?: number
    updatedBy?: string
    updatedByGroup?: UserGroup
  }

  const [uploads, setUploads] = useState<Record<string, UploadProgress> | null>(null)

  const themeData = useMemo<ThemeContextData>(
    () => ({ theme, inputSize: size, placeholderAbove, hideHints, hideLabels }),
    [theme, size, placeholderAbove, hideHints, hideLabels],
  )
  return (
    <ThemeContext.Provider value={themeData}>
      <CorrectionsProvider corrections={corrections ?? null}>
        <ShareCorrectionsProvider shared={corrections?.correctionsShared ?? null}>
          <InnerForm
            field={field}
            data={data}
            setUploads={setUploads}
            closeOnSuccess={closeOnSuccess}
            onClose={onCancel}
            onSubmit={onSubmit}
            registerForm={registerForm}>
            {({ errors, submitErrors }) => {
              const body = (
                <>
                  <FormElement
                    root
                    listIndex={0}
                    active
                    disabled={!!readOnly}
                    style={fieldStyle}
                    showName={false}
                    name={isField(field) ? 'value' : ''}
                    field={field}
                  />
                  {updatedBy || updatedOn || updatedByGroup ? (
                    <Box px={2} py={1}>
                      <ActionLog
                        action="Updated"
                        by={updatedBy}
                        on={updatedOn}
                        group={updatedByGroup}
                      />
                    </Box>
                  ) : null}
                </>
              )
              return (
                <>
                  <VStack
                    borderRadius={4}
                    align="flex-start"
                    bg="white"
                    width="100%"
                    spacing={0}
                    shadow="md"
                    {...boxProps}>
                    {BodyContainer ? (
                      <BodyContainer>{body}</BodyContainer>
                    ) : (
                      <Box p={3} w="100%" {...bodyContainerProps}>
                        {body}
                      </Box>
                    )}
                    <Box px={4}>
                      <CollapseError
                        style={{ paddingTop: '0' }}
                        error={errors?.[FORM_ERROR] || submitErrors?.[FORM_ERROR]}
                      />
                    </Box>
                    <SimpleFormFooter
                      canSubmitClean={canSubmitClean}
                      onCancel={onCancel}
                      uploads={uploads}
                      theme={theme}
                      submitText={submitText}
                      submitCleanText={submitCleanText}
                      hasResetButton={hasResetButton}
                      flexProps={footerFlexProps}>
                      {children}
                    </SimpleFormFooter>
                  </VStack>
                  {submitOnEnter ? <SubmitOnEnter /> : null}
                </>
              )
            }}
          </InnerForm>
        </ShareCorrectionsProvider>
      </CorrectionsProvider>
    </ThemeContext.Provider>
  )
}

const scrollableBodyBoxProps: BoxProps = {
  // maxH: '100%',
  width: '100%',
  h: '100%',
}

const scrollableFooterFlexProps: FlexProps = {
  bg: 'gray.100',
}

const ScrollableBodyContainer: FC<PropsWithChildren<{ bodyContainerProps?: BoxProps }>> = ({
  children,
  bodyContainerProps,
}) => (
  <Box
    w="100%"
    flex={1}
    py={2}
    px={4}
    minH={0}
    overflowY="auto"
    borderBottom="1px solid"
    borderColor="gray.200"
    {...bodyContainerProps}>
    {children}
  </Box>
)

export const ScrollableBodySimpleForm = <T extends AnyObject>({
  boxProps,
  footerFlexProps,
  ...props
}: Omit<SimpleFormProps<T>, 'BodyContainer'> & {
  boxProps?: BoxProps
  footerFlexProps?: FlexProps
}) => (
  <SimpleForm
    {...props}
    boxProps={{ ...scrollableBodyBoxProps, ...boxProps }}
    footerFlexProps={{ ...scrollableFooterFlexProps, ...footerFlexProps }}
    BodyContainer={ScrollableBodyContainer}
  />
)
